import moment from 'moment';

export enum ActivityType {
  OtherActivity,
  DeliveryOfPlan,
}

export interface Activity {
  eventId: number;
  initial: string;
  message: string | null;
  time: string;
  userName: string;
  activityName: string | null;
  activityType: ActivityType;
  comment: string | null;
  isActivity: boolean;
}

export type ActivityItem = {
  date: string;
  dayActivity: {
    initial: string;
    activities: Activity[];
  }[];
};

export interface GetActivitiesDto {
  activities: Activity[];
  lastAuditEventDate: string | null;
  lastAuditEventId: number | null;
  lastManualEventDate: string | null;
  lastManualEventId: number | null;
}

export interface RegroupedActivitiesDto extends GetActivitiesDto {
  items: ActivityItem[];
}

export interface CreateUserActivityDto {
  Id?: string;
  Date: string;
  ActivityType: ActivityType;
  ActivityName: string;
  Comment: string;
}

export interface CreateUserActivityParams {
  clientId: string;
  activity: CreateUserActivityDto;
  hideAutomatic: boolean;
}

export const DEBOUNCE_TIME = 200;

export enum UserActivitySortOrder {
  Newest,
  Oldest,
}

export interface UserActivityFilterOption {
  id: string;
  label: string;
}

export interface UserActivityFilterOptionsDTO {
  activityOptionsByUserId: Record<string, UserActivityFilterOption[]>;
  userOptions: UserActivityFilterOption[];
}

export const USER_FILTER_NONE_ID = 'user-none';

export const isSameIdSelection = (selected: string[], other: string[]) => {
  const selectedSet = new Set(selected);
  const otherSet = new Set(other);

  if (selectedSet.size !== otherSet.size) {
    return false;
  }

  return selected.every(id => otherSet.has(id));
};

export const pruneIds = (ids: string[], availableIds: string[]): string[] =>
  ids.filter(id => availableIds.includes(id));

const defaultSelectedOptionIds = (options: UserActivityFilterOption[]) =>
  options.length > 0 ? options.map(option => option.id) : [];

export const defaultFilterSelection = (filterOptions: UserActivityFilterOptionsDTO | undefined) => {
  const user = defaultSelectedOptionIds(filterOptions?.userOptions ?? []);
  const activity = defaultSelectedOptionIds(
    activityOptionsForUsers(filterOptions?.activityOptionsByUserId ?? {}, user),
  );

  return { activity, user };
};

export const isSameAppliedFilterSelection = (
  next: string[] | null,
  prev: string[] | null,
): boolean => (next === null ? prev === null : prev !== null && isSameIdSelection(next, prev));

export const selectedIdsForQuery = (selected: string[], defaultIds: string[]) =>
  isSameIdSelection(selected, defaultIds) ? [] : selected;

/** Maps applied user ids to the API form value; empty custom selection sends the no-users sentinel. */
export const userIdsForQuery = (
  selected: string[],
  defaultIds: string[],
  isCustomUserSelection: boolean,
): string[] => {
  if (isSameIdSelection(selected, defaultIds)) {
    return [];
  }

  if (selected.length === 0 && isCustomUserSelection && defaultIds.length > 0) {
    return [USER_FILTER_NONE_ID];
  }

  return selected;
};

/** null means "all options selected" for the current FilterOptions response. */
export const normalizeAppliedFilterSelection = (
  userIds: string[],
  activityIds: string[],
  filterOptions: UserActivityFilterOptionsDTO | undefined,
): { user: string[] | null; activity: string[] | null } => {
  const defaults = defaultFilterSelection(filterOptions);

  const normalizedUser =
    userIds.length === 0 && defaults.user.length > 0
      ? null
      : isSameIdSelection(userIds, defaults.user)
      ? null
      : userIds;

  return {
    user: normalizedUser,
    activity: isSameIdSelection(activityIds, defaults.activity) ? null : activityIds,
  };
};

export const reconcileAppliedFilterSelection = (
  filterOptions: UserActivityFilterOptionsDTO,
  prevUser: string[] | null,
  prevActivity: string[] | null,
): { user: string[] | null; activity: string[] | null } => {
  const { user: defaultUser, activity: defaultActivity } = defaultFilterSelection(filterOptions);

  let nextUser = prevUser;
  if (prevUser !== null) {
    const prunedUser = pruneIds(prevUser, defaultUser);
    if (prunedUser.length === 0) {
      nextUser = null;
    } else {
      nextUser = isSameIdSelection(prunedUser, defaultUser) ? null : prunedUser;
    }
  }

  const scopedUser = nextUser ?? defaultUser;
  const availableActivityIds = activityOptionsForUsers(
    filterOptions.activityOptionsByUserId,
    scopedUser,
  ).map(option => option.id);

  let nextActivity = prevActivity;
  if (prevActivity !== null) {
    const prunedActivity = pruneIds(prevActivity, availableActivityIds);
    if (prunedActivity.length === 0) {
      nextActivity = null;
    } else {
      nextActivity = isSameIdSelection(prunedActivity, defaultActivity) ? null : prunedActivity;
    }
  }

  return { user: nextUser, activity: nextActivity };
};

export const activityOptionsForUsers = (
  activityOptionsByUserId: Record<string, UserActivityFilterOption[]>,
  selectedUserIds: string[],
): UserActivityFilterOption[] => {
  const seen = new Set<string>();
  const result: UserActivityFilterOption[] = [];

  for (const userId of selectedUserIds) {
    for (const option of activityOptionsByUserId[userId] ?? []) {
      if (!seen.has(option.id)) {
        seen.add(option.id);
        result.push(option);
      }
    }
  }

  return result.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
};

export interface UserActivitySortFilterApply {
  sortOrder: UserActivitySortOrder;
  selectedActivityFilterIds: string[];
  selectedUserFilterIds: string[];
}

const colors = ADV.settings.color_groups.reduce((acc, cur) => acc.concat(cur), []) as string[];
const pickedColors = [] as { initials: string; color: string }[];

export const draftColor = (initials: string): string => {
  const pick = Math.floor(Math.random() * colors.length);
  const color = colors[pick];

  const drafted = pickedColors.find(draft => draft.initials === initials);

  if (drafted) {
    return drafted.color;
  }

  colors.splice(pick, 1);
  pickedColors.push({
    initials,
    color,
  });

  return color;
};

type RegroupItemsStep1 = Activity[];
type RegroupItemsStep2 = {
  date: string;
  dayActivity: Record<string, Activity[]>;
}[];
type RegroupItemsStep3 = ActivityItem[];
/**
 * Due to timezone differences, some activities might belong to a different day in local time.
 * This function parses the response and regroups the activities considering local time.
 *
 * (Note: Times in response are UTC)
 */
export const regroupItems = (data: GetActivitiesDto): RegroupedActivitiesDto => {
  const step1: RegroupItemsStep1 = [...(data.activities ?? [])];

  // Step 2 ➜ Regroup activities by local date.
  const step2: RegroupItemsStep2 = step1.reduce((newItemsAcc, activity) => {
    // event time using local timezone
    const localTime = moment.utc(activity.time).local();
    // start of the day time using local timezone
    const startOfDay = moment(localTime).startOf('day');

    // last Item in the accumulator
    const lastItem = newItemsAcc.length > 0 && newItemsAcc[newItemsAcc.length - 1];
    // current day in the accumulator (last one)
    const currentDay = lastItem && moment(lastItem.date);

    // take the chance to manipulate the activity (e.g. to use the local time)
    const parsedActivity = { ...activity, time: localTime.format() };

    // If the day is different in local time, create a new date entry
    if (!currentDay || !startOfDay.isSame(currentDay)) {
      return [
        ...newItemsAcc,
        {
          date: startOfDay.format(),
          dayActivity: {
            [activity.initial]: [parsedActivity],
          },
        },
      ];
    }

    if (lastItem) {
      return [
        ...newItemsAcc.slice(0, newItemsAcc.length - 1),
        {
          ...lastItem,
          dayActivity: {
            ...lastItem.dayActivity,
            [activity.initial]: [...(lastItem.dayActivity[activity.initial] || []), parsedActivity],
          },
        },
      ];
    }

    return newItemsAcc;
  }, [] as RegroupItemsStep2);

  // Step 3 ➜ Convert to the expected structure (aka ActivityItem[])
  const step3: RegroupItemsStep3 = step2.map(({ date, dayActivity }) => ({
    date,
    dayActivity: Object.keys(dayActivity).map(initial => ({
      initial,
      activities: dayActivity[initial],
    })),
  }));

  return { ...data, items: step3 };
};

const activityDedupKey = (activity: Activity) =>
  `${activity.isActivity ? 'manual' : 'audit'}:${activity.eventId}`;

const compareActivitiesByTime = (
  a: Activity,
  b: Activity,
  sortOrder: UserActivitySortOrder,
): number => {
  const diff = moment(a.time).valueOf() - moment(b.time).valueOf();
  return sortOrder === UserActivitySortOrder.Newest ? -diff : diff;
};

/**
 * Merges paginated activity log pages so the same local date is not rendered twice.
 * Pages are processed in fetch order; activities on a shared date are combined per user
 * and re-sorted by timestamp so order stays correct when a day spans multiple pages.
 */
export const mergeActivityPages = (
  pages: RegroupedActivitiesDto[],
  sortOrder: UserActivitySortOrder,
): ActivityItem[] => {
  const dateOrder: string[] = [];
  const byDate = new Map<string, Map<string, Activity[]>>();

  for (const { items } of pages) {
    for (const { date, dayActivity } of items) {
      if (!byDate.has(date)) {
        dateOrder.push(date);
        byDate.set(date, new Map());
      }

      const byInitial = byDate.get(date)!;

      for (const { initial, activities } of dayActivity) {
        const existing = byInitial.get(initial) ?? [];
        const seenKeys = new Set(existing.map(activityDedupKey));
        const merged = [...existing];

        for (const activity of activities) {
          const key = activityDedupKey(activity);
          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            merged.push(activity);
          }
        }

        byInitial.set(initial, merged);
      }
    }
  }

  return dateOrder.map(date => ({
    date,
    dayActivity: Array.from(byDate.get(date)!, ([initial, activities]) => ({
      initial,
      activities: [...activities].sort((a, b) => compareActivitiesByTime(a, b, sortOrder)),
    })),
  }));
};
